/**
 * Activity Tracking Hook
 * Handles session heartbeat, page views, and user activity logging
 */
import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
const EVENT_BATCH_INTERVAL = 30 * 1000; // 30 seconds

// Event buffer for batching
let eventBuffer = [];
let lastPageView = null;

export const useActivityTracking = (token, isAuthenticated) => {
  const location = useLocation();
  const heartbeatRef = useRef(null);
  const batchRef = useRef(null);
  const sessionStartedRef = useRef(false);

  // Start session on mount
  const startSession = useCallback(async () => {
    if (!token || sessionStartedRef.current) return;
    
    try {
      const response = await fetch(`${API}/activity/session/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        sessionStartedRef.current = true;
        console.log('[Activity] Session started');
      }
    } catch (error) {
      console.error('[Activity] Failed to start session:', error);
    }
  }, [token]);

  // End session on unmount/logout
  const endSession = useCallback(async () => {
    if (!token || !sessionStartedRef.current) return;
    
    try {
      await fetch(`${API}/activity/session/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      sessionStartedRef.current = false;
      console.log('[Activity] Session ended');
    } catch (error) {
      console.error('[Activity] Failed to end session:', error);
    }
  }, [token]);

  // Send heartbeat
  const sendHeartbeat = useCallback(async () => {
    if (!token) return;
    
    try {
      await fetch(`${API}/activity/session/heartbeat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('[Activity] Heartbeat failed:', error);
    }
  }, [token]);

  // Log single event
  const logEvent = useCallback(async (eventType, eventData = {}) => {
    if (!token) return;
    
    eventBuffer.push({ event_type: eventType, event_data: eventData });
    
    // Flush immediately for important events
    if (['task_created', 'task_completed', 'message_sent'].includes(eventType)) {
      await flushEvents(token);
    }
  }, [token]);

  // Flush event buffer
  const flushEvents = async (authToken) => {
    if (eventBuffer.length === 0 || !authToken) return;
    
    const events = [...eventBuffer];
    eventBuffer = [];
    
    try {
      await fetch(`${API}/activity/events/batch`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(events)
      });
    } catch (error) {
      // Put events back on failure
      eventBuffer = [...events, ...eventBuffer];
      console.error('[Activity] Failed to flush events:', error);
    }
  };

  // Log page view
  const logPageView = useCallback((pathname) => {
    if (!token || pathname === lastPageView) return;
    
    lastPageView = pathname;
    
    // Map pathname to friendly page name
    const pageNames = {
      '/': 'Dashboard',
      '/tasks': 'Task List',
      '/completed': 'Completed Tasks',
      '/history': 'Task History',
      '/groups': 'Groups',
      '/bundles': 'Task Bundles',
      '/templates': 'Templates',
      '/company': 'Company Management',
      '/settings': 'Settings',
      '/intake-forms': 'Intake Forms',
      '/group-assignments': 'Group Assignments',
      '/team-activity': 'Team Activity'
    };
    
    const pageName = pageNames[pathname] || pathname;
    
    eventBuffer.push({
      event_type: 'page_view',
      event_data: { page: pathname, page_name: pageName }
    });
  }, [token]);

  // Track page views on route change
  useEffect(() => {
    if (isAuthenticated && token) {
      logPageView(location.pathname);
    }
  }, [location.pathname, isAuthenticated, token, logPageView]);

  // Setup session and heartbeat
  useEffect(() => {
    if (!isAuthenticated || !token) {
      // Clear intervals if logged out
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      if (batchRef.current) {
        clearInterval(batchRef.current);
        batchRef.current = null;
      }
      return;
    }

    // Start session
    startSession();

    // Setup heartbeat interval
    heartbeatRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
    
    // Setup event batch flush interval
    batchRef.current = setInterval(() => flushEvents(token), EVENT_BATCH_INTERVAL);

    // Cleanup on unmount or logout
    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
      }
      if (batchRef.current) {
        clearInterval(batchRef.current);
      }
      // Flush remaining events
      flushEvents(token);
    };
  }, [isAuthenticated, token, startSession, sendHeartbeat]);

  // End session on page unload
  useEffect(() => {
    const handleUnload = () => {
      if (token && sessionStartedRef.current) {
        // Use sendBeacon for reliable delivery on page close
        navigator.sendBeacon(
          `${API}/activity/session/end`,
          JSON.stringify({})
        );
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [token]);

  return {
    logEvent,
    logPageView,
    endSession
  };
};

export default useActivityTracking;
